/*
 * Tenancy detail — Activity / Usage / Support / Billing view for a single
 * workspace. Mounted at four routes that map to one component with a
 * `view` prop so the IA reads cleanly:
 *   /admin/workspaces/:workspaceId/activity
 *   /admin/workspaces/:workspaceId/usage
 *   /admin/workspaces/:workspaceId/support
 *   /admin/workspaces/:workspaceId/billing
 *
 * Each view is a thin wrapper over existing endpoints:
 *   activity → /api/jobs filtered by workspaceId
 *   usage    → /api/saas/workspaces/:id/plan
 *   support  → /api/audit + /api/jobs failures
 *   billing  → /api/admin/workspaces/:id/payments + plan detail
 */

import { useEffect, useMemo, useState } from "react";
import { SkeletonTable } from "../components/Skeleton.js";
import StatusBadge from "../components/StatusBadge.js";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import { useParams, Link } from "react-router-dom";
import { api, ApiError } from "../lib/api.js";

type Job = { id: string; type: string; status: string; workspaceId?: string; startedAt?: string; requestedAt: string; completedAt?: string; error?: string };
type WorkspaceDetail = Awaited<ReturnType<typeof api.workspacePlanDetail>>;
type PaymentRecord = Awaited<ReturnType<typeof api.listWorkspacePaymentRecords>>["records"][number];

export interface TenancyDetailProps {
  view: "activity" | "usage" | "support" | "billing";
}

export default function TenancyDetail({ view }: TenancyDetailProps) {
  const { workspaceId = "" } = useParams<{ workspaceId: string }>();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [detail, setDetail] = useState<WorkspaceDetail | null>(null);
  const [payments, setPayments] = useState<PaymentRecord[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!workspaceId) return;
    api.jobs().then((r) => setJobs((r.jobs as unknown as Job[]).filter((j) => j.workspaceId === workspaceId))).catch(() => {});
    api.workspacePlanDetail(workspaceId).then(setDetail).catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
    if (view === "billing") {
      api.listWorkspacePaymentRecords(workspaceId)
        .then((r) => setPayments(r.records))
        .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
    }
  }, [workspaceId, view]);

  const failures = useMemo(() => jobs.filter((j) => j.status === "failed" || j.status === "dead-lettered"), [jobs]);

  if (!workspaceId) {
    return (
      <div className="page">
        <div className="ts-alert ts-alert-error">No workspace specified.</div>
      </div>
    );
  }

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Workspace {view}</h1>
          <p className="muted">
            <Link to={`/admin/workspaces/${workspaceId}`}>← back to workspace</Link> · <code>{workspaceId}</code>
          </p>
          <nav style={{ display: "flex", gap: 12, marginTop: 8, fontSize: 13 }} aria-label="Workspace detail views">
            {(["activity", "usage", "billing", "support"] as const).map((v) => (
              <Link
                key={v}
                to={`/admin/workspaces/${workspaceId}/${v}`}
                style={{
                  textTransform: "capitalize",
                  fontWeight: v === view ? 600 : 400,
                  textDecoration: v === view ? "underline" : "none"
                }}
              >{v}</Link>
            ))}
          </nav>
        </div>
      </header>

      {error && <div className="ts-alert ts-alert-error">{error}</div>}

      {view === "activity" && (
        <>
          <section className="stat-grid">
            <div className="stat"><div className="stat-label">Jobs (recent)</div><div className="stat-value">{jobs.length}</div></div>
            <div className="stat"><div className="stat-label">Failures</div><div className="stat-value" style={{ color: failures.length > 0 ? "#b91c1c" : undefined }}>{failures.length}</div></div>
          </section>
          <section className="section">
            <div className="section-head"><h2>Recent jobs</h2></div>
            {jobs.length === 0 ? (
              <div className="empty-state-card"><h2 style={{ marginTop: 0 }}>No jobs yet</h2><p>This workspace hasn't run any jobs.</p></div>
            ) : (
              <table className="transfer-table">
                <thead><tr><th>When</th><th>Type</th><th>Status</th><th>Error</th></tr></thead>
                <tbody>
                  {jobs.slice(0, 50).map((j) => (
                    <tr key={j.id}>
                      <td className="muted" style={{ fontSize: 12 }}>{j.startedAt ?? j.requestedAt}</td>
                      <td className="mono" style={{ fontSize: 12 }}>{j.type}</td>
                      <td><StatusBadge status={j.status} /></td>
                      <td className="error-text" style={{ fontSize: 12, maxWidth: 480 }}>{j.error ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}

      {view === "usage" && (
        <>
          {!detail && <SkeletonTable rows={5} columns={4} />}
          {detail && (
            <section className="stat-grid">
              <div className="stat"><div className="stat-label">Plan</div><div className="stat-value">{detail.plan?.name ?? "—"}</div><div className="stat-hint">{detail.subscriptionActive ? "active" : "inactive"}</div></div>
              <div className="stat"><div className="stat-label">Seats</div><div className="stat-value">{detail.usage.seats}</div></div>
              <div className="stat"><div className="stat-label">Datasets</div><div className="stat-value">{detail.usage.datasets}</div></div>
              <div className="stat"><div className="stat-label">DHIS2 instances</div><div className="stat-value">{detail.usage.dhis2Instances}</div></div>
              <div className="stat"><div className="stat-label">Public portals</div><div className="stat-value">{detail.usage.publicPortals}</div></div>
            </section>
          )}
          {detail && Object.keys(detail.limits).length > 0 && (
            <section className="section">
              <div className="section-head"><h2>Limits</h2></div>
              <table className="transfer-table">
                <thead><tr><th>Limit</th><th style={{ textAlign: "right" }}>Value</th></tr></thead>
                <tbody>
                  {Object.entries(detail.limits).map(([k, v]) => (
                    <tr key={k}>
                      <td className="mono" style={{ fontSize: 12 }}>{k}</td>
                      <td style={{ textAlign: "right" }}>{String(v)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </>
      )}

      {view === "billing" && (
        <>
          {!detail && <SkeletonTable rows={3} columns={4} />}
          {detail && (
            <section className="stat-grid">
              <div className="stat">
                <div className="stat-label">Plan</div>
                <div className="stat-value">{detail.plan?.name ?? "—"}</div>
                <div className="stat-hint">{detail.subscriptionActive ? "active" : "inactive"}</div>
              </div>
              <div className="stat">
                <div className="stat-label">Subscription</div>
                <div className="stat-value">{detail.subscription?.status ?? "—"}</div>
                <div className="stat-hint">{detail.plan?.price ? `$${detail.plan.price}/mo list price` : "—"}</div>
              </div>
              <div className="stat">
                <div className="stat-label">Recorded payments</div>
                <div className="stat-value">{payments?.length ?? "—"}</div>
                <div className="stat-hint">all-time</div>
              </div>
              <div className="stat">
                <div className="stat-label">Completed payments</div>
                <div className="stat-value">{(payments ?? []).filter((p) => p.status === "completed").length}</div>
                <div className="stat-hint">excludes refunded / failed</div>
              </div>
            </section>
          )}

          <section className="section" style={{ marginTop: 16 }}>
            <div className="section-head"><h2>Payment history</h2></div>
            {payments === null && <SkeletonTable rows={5} columns={6} />}
            {payments !== null && payments.length === 0 && (
              <div className="empty-state-card">
                <h2 style={{ marginTop: 0 }}>No payments recorded for this workspace</h2>
                <p>
                  Workspace payments land here once a payment provider is configured under{" "}
                  <Link to="/admin/administration/monetisation/payment-gateways">Payment gateways</Link>{" "}
                  and the corresponding webhook fires for this workspace.
                </p>
              </div>
            )}
            {payments !== null && payments.length > 0 && (
              <table className="transfer-table">
                <thead>
                  <tr>
                    <th>Initiated</th>
                    <th>Completed</th>
                    <th>Plan</th>
                    <th>Provider</th>
                    <th>Reference</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => {
                    const fmt = (iso?: string | null) => {
                      if (!iso) return "—";
                      try { return new Date(iso).toLocaleString(); } catch { return iso ?? "—"; }
                    };
                    const amt = p.amount == null ? "—" : `${(p.currency ?? "USD").toUpperCase()} ${Number(p.amount).toFixed(2)}`;
                    const cls =
                      p.status === "completed" ? "pill pill-ok"
                      : p.status === "refunded" ? "pill pill-info"
                      : p.status === "failed" ? "pill pill-err"
                      : "pill pill-muted";
                    return (
                      <tr key={p.id}>
                        <td className="muted" style={{ fontSize: 12 }}>{fmt(p.initiatedAt)}</td>
                        <td className="muted" style={{ fontSize: 12 }}>{fmt(p.completedAt)}</td>
                        <td><code style={{ fontSize: 11 }}>{p.planId}</code></td>
                        <td>{p.providerName}</td>
                        <td className="mono" style={{ fontSize: 11 }}>{p.providerReference ?? "—"}</td>
                        <td>{amt}</td>
                        <td><span className={cls}>{p.status}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}

      {view === "support" && (
        <>
          <section className="empty-state-card">
            <h2 style={{ marginTop: 0 }}>Support summary for this workspace</h2>
            <p>
              Failures in the last 24h: <strong>{failures.length}</strong>.
              {failures.length > 0 && " Investigate via the Failures & dead-letter page."}
            </p>
          </section>
          {failures.length > 0 && (
            <section className="section">
              <div className="section-head"><h2>Recent failures</h2></div>
              <table className="transfer-table">
                <thead><tr><th>When</th><th>Type</th><th>Error</th></tr></thead>
                <tbody>
                  {failures.slice(0, 50).map((j) => (
                    <tr key={j.id}>
                      <td className="muted" style={{ fontSize: 12 }}>{j.completedAt ?? j.startedAt}</td>
                      <td className="mono" style={{ fontSize: 12 }}>{j.type}</td>
                      <td className="error-text" style={{ fontSize: 12, maxWidth: 600 }}>{j.error ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </>
      )}
    </div>
  );
}
